import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import './App.css';

import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import OpenAccount from './pages/OpenAccount';
import KycOnboarding from './pages/public/KycOnboarding';
import Layout from './components/Layout';

import CustomerDashboard from './pages/customer/Dashboard';
import Onboarding from './pages/customer/Onboarding';
import Statement from './pages/customer/Statement';
import InternalTransfer from './pages/customer/InternalTransfer';
import Security from './pages/customer/Security';
import PixReceive from './pages/customer/PixReceive';
import PixSend from './pages/customer/PixSend';
import BoletoPay from './pages/customer/BoletoPay';
import Overdraft from './pages/customer/Overdraft';
import Loans from './pages/customer/Loans';

import BankerDashboard from './pages/banker/Dashboard';
import Customers from './pages/banker/Customers';
import CustomerDetail from './pages/banker/CustomerDetail';
import Transactions from './pages/banker/Transactions';
import PixReceipts from './pages/banker/PixReceipts';
import Limits from './pages/banker/Limits';
import LoansManagement from './pages/banker/LoansManagement';
import ExternalEvents from './pages/banker/ExternalEvents';

import PlatformDashboard from './pages/platform/Dashboard';
import PlatformTenants from './pages/platform/Tenants';

const ADMIN_ROLES = ['Manager', 'Operator'];
const PLATFORM_ROLE = 'SmartPayAdmin';
const CUSTOMER_ROLE = 'Customer';

const homeForRole = (role) => {
  if (role === PLATFORM_ROLE) return '/platform/dashboard';
  if (ADMIN_ROLES.includes(role)) return '/admin/dashboard';
  return '/app/dashboard';
};

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to={homeForRole(user.role)} replace />;
  return <Layout>{children}</Layout>;
};

const CustomerRouteWithOnboarding = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkOnboardingStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setLoading(false); return; }
        const { default: api } = await import('./services/api');
        const response = await api.get('/me/onboarding-status');
        setNeedsOnboarding(!response.data.can_access_financial_features);
      } catch (error) {
        console.error('Error checking onboarding:', error);
        setNeedsOnboarding(true);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === CUSTOMER_ROLE) checkOnboardingStatus();
    else setLoading(false);
  }, [user]);

  if (authLoading) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== CUSTOMER_ROLE) return <Navigate to={homeForRole(user.role)} replace />;
  if (loading) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;
  if (needsOnboarding && window.location.pathname !== '/customer/onboarding') return <Navigate to="/customer/onboarding" replace />;
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/open-account" element={<OpenAccount />} />
            <Route path="/onboarding/:token" element={<KycOnboarding />} />

            <Route path="/platform/dashboard" element={<PrivateRoute allowedRoles={[PLATFORM_ROLE]}><PlatformDashboard /></PrivateRoute>} />
            <Route path="/platform/tenants" element={<PrivateRoute allowedRoles={[PLATFORM_ROLE]}><PlatformTenants /></PrivateRoute>} />

            <Route path="/customer/onboarding" element={<PrivateRoute allowedRoles={[CUSTOMER_ROLE]}><Onboarding /></PrivateRoute>} />
            <Route path="/app/dashboard" element={<CustomerRouteWithOnboarding><CustomerDashboard /></CustomerRouteWithOnboarding>} />
            <Route path="/app/statement" element={<CustomerRouteWithOnboarding><Statement /></CustomerRouteWithOnboarding>} />
            <Route path="/app/transfers/internal" element={<CustomerRouteWithOnboarding><InternalTransfer /></CustomerRouteWithOnboarding>} />
            <Route path="/app/security" element={<CustomerRouteWithOnboarding><Security /></CustomerRouteWithOnboarding>} />
            <Route path="/app/pix/receive" element={<CustomerRouteWithOnboarding><PixReceive /></CustomerRouteWithOnboarding>} />
            <Route path="/app/pix/send" element={<CustomerRouteWithOnboarding><PixSend /></CustomerRouteWithOnboarding>} />
            <Route path="/app/boleto/pay" element={<CustomerRouteWithOnboarding><BoletoPay /></CustomerRouteWithOnboarding>} />
            <Route path="/app/credit/overdraft" element={<CustomerRouteWithOnboarding><Overdraft /></CustomerRouteWithOnboarding>} />
            <Route path="/app/credit/loans" element={<CustomerRouteWithOnboarding><Loans /></CustomerRouteWithOnboarding>} />

            <Route path="/admin/dashboard" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><BankerDashboard /></PrivateRoute>} />
            <Route path="/admin/customers" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><Customers /></PrivateRoute>} />
            <Route path="/admin/customers/:id" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><CustomerDetail /></PrivateRoute>} />
            <Route path="/admin/transactions" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><Transactions /></PrivateRoute>} />
            <Route path="/admin/pix/receipts" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><PixReceipts /></PrivateRoute>} />
            <Route path="/admin/limits" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><Limits /></PrivateRoute>} />
            <Route path="/admin/loans" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><LoansManagement /></PrivateRoute>} />
            <Route path="/admin/external-events" element={<PrivateRoute allowedRoles={ADMIN_ROLES}><ExternalEvents /></PrivateRoute>} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
